import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Dialog } from 'primereact/dialog';
import apiFetch from '../../services/apiFetch';
import './CourseKeywordsModal.css';

export function parseKeywords(raw) {
    return (raw || '')
        .split(',')
        .map(k => k.trim())
        .filter(Boolean);
}

export default function CourseKeywordsModal({ visible, course, token, onClose }) {
    const [value, setValue] = useState('');
    const [saving, setSaving] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    // Re-seed from the course every time the modal opens
    useEffect(() => {
        if (visible) {
            setValue(course?.searchKeywords || '');
            setSaving(false);
            setGenerating(false);
            setErrorMsg('');
        }
    }, [visible, course?.id, course?.searchKeywords]);

    const keywords = parseKeywords(value);

    const handleCancel = useCallback(() => {
        onClose({ courseId: course?.id, updated: false, searchKeywords: null });
    }, [onClose, course?.id]);

    const generate = useCallback(async () => {
        if (!course?.id) return;
        setErrorMsg('');
        setGenerating(true);
        try {
            const res = await apiFetch(
                `/api/courses/${course.id}/suggest-keywords`,
                token,
                { method: 'POST' },
            );
            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData.error || `Keyword generation failed (${res.status})`);
            }
            const data = await res.json();
            setValue((data.keywords || []).join(', '));
        } catch (err) {
            setErrorMsg(err.message || 'Keyword generation failed, please try again.');
        } finally {
            setGenerating(false);
        }
    }, [course?.id, token]);

    const save = useCallback(async () => {
        if (!course?.id) return;
        setErrorMsg('');
        setSaving(true);
        const cleaned = parseKeywords(value).join(', ');
        try {
            const res = await apiFetch(
                `/api/courses/${course.id}`,
                token,
                { method: 'PUT', body: JSON.stringify({ search_keywords: cleaned }) },
            );
            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData.error || `Saving failed (${res.status})`);
            }
            onClose({ courseId: course.id, updated: true, searchKeywords: cleaned });
        } catch (err) {
            setErrorMsg(err.message || 'Saving failed, please try again.');
            setSaving(false);
        }
    }, [course?.id, token, value, onClose]);

    const busy = saving || generating;

    return (
        <Dialog
            visible={visible}
            header={course?.name ? `Search keywords: ${course.name}` : 'Search keywords'}
            modal
            style={{ width: '520px', maxWidth: '95vw' }}
            onHide={handleCancel}
            className="course-keywords-dialog"
        >
            <div className="ckm-body">
                <p className="ckm-desc">
                    Comma-separated aliases users might type when searching for this
                    course in the app, e.g. <em>ww2, brain, cognitive biases</em>.
                </p>
                <textarea
                    className="ckm-textarea"
                    rows={3}
                    value={value}
                    disabled={busy}
                    onChange={e => setValue(e.target.value)}
                    placeholder="keyword one, keyword two, …"
                />
                {keywords.length > 0 && (
                    <div className="ckm-preview">
                        {keywords.map((kw, i) => (
                            <span key={i} className="ckm-pill">{kw}</span>
                        ))}
                    </div>
                )}
                {errorMsg && <div className="ckm-error">{errorMsg}</div>}
                <div className="ckm-actions">
                    <button
                        type="button"
                        className="ckm-btn ckm-btn-generate"
                        disabled={busy}
                        onClick={generate}
                    >
                        <i className={`pi ${generating ? 'pi-spin pi-spinner' : 'pi-tags'}`} />
                        {generating ? 'Generating…' : 'Generate with AI'}
                    </button>
                    <div className="ckm-actions-right">
                        <button type="button" className="ckm-btn" disabled={busy} onClick={handleCancel}>
                            Cancel
                        </button>
                        <button type="button" className="ckm-btn ckm-btn-primary" disabled={busy} onClick={save}>
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>
        </Dialog>
    );
}

CourseKeywordsModal.propTypes = {
    visible: PropTypes.bool.isRequired,
    course: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        searchKeywords: PropTypes.string,
    }),
    token: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
};

CourseKeywordsModal.defaultProps = {
    course: null,
};
